import { alert } from '@/services/alert';
import { Link, useParams } from 'react-router';
import { useDebouncedCallback } from '@mantine/hooks';
import { RichTextInput } from '@/components/RichTextInput';
import { useMedplum } from '@medplum/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { formatToAdobeSpectrumFormat } from '@/services/date';
import { getCodeableConceptCode } from '@bariendo/lib/services';
import { AppointmentTimelineGroup, TimelineAppointment } from '@/components/Timeline/types';
import { Appointment, Coding, Patient, Practitioner, Reference } from '@medplum/fhirtypes';
import { isForbiddenError, isNotFoundError } from '@bariendo/lib/error';
import { reportError } from '@/services/error-reporting';
import {
  APPOINTMENT_STATUS_OPTIONS,
  IV_THERAPY_APPOINTMENT_TYPE_CODE,
  TIMELINE_CREATE_ITEM_QUERY_PARAM,
  TIMELINE_FOCUS_ITEM_QUERY_PARAM,
} from '@bariendo/lib/constants';
import {
  AppointmentOutcome,
  getAppointmentOutcome,
  getAppointmentStatusDisplay,
  isAppointmentStatusOptionSelected,
} from '@/pages/SchedulingPage/utils/appointments';
import { TimelineCard } from '@/components/Timeline/TimelineItems/TimelineCard';
import { AppointmentOrganizationFragment } from '@/pages/PatientTimelinePage/graphql';
import { HighlightableItem } from '@/components/Timeline/TimelineItems/HighlightableItem';
import { extractPatientsFromAppointment, extractPractitionersFromAppointment } from '@bariendo/lib/scheduling';
import {
  IconChevronDown,
  IconChevronRight,
  IconCircleCheck,
  IconClipboardText,
  IconCopy,
  IconExternalLink,
} from '@tabler/icons-react';
import {
  createReference,
  formatAddress,
  getReferenceString,
  formatHumanName,
  resolveId,
  isResource,
  PatchOperation,
} from '@medplum/core';
import {
  Avatar,
  Anchor,
  Badge,
  Box,
  Button,
  Flex,
  Group,
  Loader,
  Menu,
  Stack,
  Text,
  Tooltip,
  ActionIcon,
  Table,
  Collapse,
} from '@mantine/core';
import { useAppointment } from '@/hooks/useAppointment';
import { useZoomHostLink } from '@/hooks/useZoomHostLink';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { PatientAccessChartLink } from '@/components/Timeline/TimelineItems/PatientAccessCall/PatientAccessChartLink';
import { patientAccessCardKindForUser } from '@/components/Timeline/TimelineItems/PatientAccessCall/patientAccessCardKind';
import { TeammateAvatar } from '@/components/TeamAvatar';

interface AppointmentHeaderProps {
  group: AppointmentTimelineGroup;
  activityCodeSystem: Coding[] | null;
  onClick?: (groupId: string) => void;
}

interface AppointmentTimelineItemProps {
  group: AppointmentTimelineGroup;
  activityCodeSystem: Coding[];
  highlightRef?: string | null;
  collapsed?: boolean;
  onCollapseToggle?: (groupId: string) => void;
  onStatusChange?: (updatedAppointment: Appointment) => void;
}

interface ZoomHostLinkDisplayProps {
  isLoading: boolean;
  isWithinWindow: boolean;
  isPast: boolean;
  hostUrl: string | null;
}

interface VideoCallContactTypeProps {
  joinUrl: string | undefined;
  appointmentStart: string | undefined;
}

interface AppointmentContactTypeProps {
  appointmentItem: TimelineAppointment;
  isIvTherapy: boolean;
  partner: AppointmentOrganizationFragment | undefined;
}

const OUTCOME_BADGE_COLORS: Record<AppointmentOutcome, string> = {
  cancelled: 'red',
  rescheduled: 'orange',
  noshow: 'gray',
};

function LabelText({ children, ...props }: { children: React.ReactNode }) {
  return (
    <Text size="md" fw={600} {...props}>
      {children}
    </Text>
  );
}

function getAppointmentTypeText(appointment: Appointment, activityCodeSystem: Coding[] | null) {
  const appointmentTypeCode = appointment.appointmentType?.coding?.[0].code;
  const appointmentTypeFromCodeSystem = activityCodeSystem?.find((code) => code.code === appointmentTypeCode);
  return (
    appointmentTypeFromCodeSystem?.display ||
    appointment.appointmentType?.text ||
    appointment.appointmentType?.coding?.[0]?.display
  );
}

function ChartingLink({ appointment }: { appointment: Appointment }) {
  const medplum = useMedplum();
  const {
    currentUserPermissions: { canViewClinicalRecord },
  } = useCurrentUser();
  const patients = extractPatientsFromAppointment(appointment);
  const partner = appointment.supportingInformation?.find((info) => isResource(info.resource, 'Organization'))
    ?.resource as AppointmentOrganizationFragment | undefined;
  const appointmentRef = appointment.id ? getReferenceString(appointment) : undefined;
  const patientsLength = patients.length;
  const hasPartner = Boolean(partner);
  const [encounter, setEncounter] = useState<Reference | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchEncounter() {
      // A role without the Encounter grant (Patient Access) gets no chart row at
      // all: the search would be refused, and "Create a new chart note" would
      // lead to a note it can never save.
      if (!appointmentRef || patientsLength !== 1 || hasPartner || !canViewClinicalRecord) {
        setIsLoading(false);
        return;
      }
      try {
        const foundEncounter = await medplum.searchOne('Encounter', {
          appointment: appointmentRef,
        });

        if (foundEncounter) {
          setEncounter(createReference(foundEncounter));
        }
      } catch (error) {
        // Forbidden is the policy answering, not a fault worth a Sentry event.
        if (!isNotFoundError(error) && !isForbiddenError(error)) {
          reportError(error, { component: 'ChartingLink', extra: { appointment: appointmentRef } });
        }
      } finally {
        setIsLoading(false);
      }
    }

    void fetchEncounter();
  }, [appointmentRef, hasPartner, medplum, patientsLength, canViewClinicalRecord]);

  if (patients.length !== 1 || !appointment.id || !!partner) {
    return null;
  }

  if (!canViewClinicalRecord) {
    return null;
  }

  if (isLoading) {
    return <Loader size="xs" />;
  }

  const patient = patients[0];
  const resolvedPatientId = resolveId(patient);
  const searchParams = new URLSearchParams();
  const patientTimelineUrl = `/Patient/${resolvedPatientId}/timeline`;

  if (encounter?.reference) {
    searchParams.set(TIMELINE_FOCUS_ITEM_QUERY_PARAM, encounter.reference);
    const url = `${patientTimelineUrl}?${searchParams.toString()}`;

    return (
      <Flex gap="xs" align="center">
        <IconClipboardText size={16} />
        <Anchor component={Link} to={url}>
          View chart
        </Anchor>
      </Flex>
    );
  }

  // A chart note already written stays reachable, but a cancelled visit is not
  // one to start charting.
  if (appointment.status === 'cancelled') {
    return null;
  }

  searchParams.set(TIMELINE_CREATE_ITEM_QUERY_PARAM, 'chart');
  searchParams.set('appointment', appointment.id);

  const url = `${patientTimelineUrl}?${searchParams.toString()}`;

  return (
    <Flex gap="xs" align="center">
      <IconClipboardText size={16} />
      <Anchor component={Link} to={url}>
        Create a new chart note
      </Anchor>
    </Flex>
  );
}

function AppointmentNotes({ appointment }: { appointment: Appointment }) {
  const medplum = useMedplum();
  const [isOpen, setIsOpen] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  const prevVersionIdRef = useRef<string | undefined>(null);
  const savedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialContentRef = useRef<{ text: string; jsonStr: string } | null>(null);

  useEffect(() => {
    return () => {
      if (savedTimeoutRef.current) {
        clearTimeout(savedTimeoutRef.current);
      }
    };
  }, []);

  // Sync when appointment is updated externally (e.g., live update from another user)
  useEffect(() => {
    if (appointment?.id) {
      const versionId = appointment.meta?.versionId;
      if (prevVersionIdRef.current !== undefined && prevVersionIdRef.current !== versionId) {
        // Appointment was updated externally - sync our ref to avoid redundant save
        const text = appointment.comment ?? '';
        const jsonStr = appointment.description ?? '{}';
        initialContentRef.current = { text, jsonStr };
      }
      prevVersionIdRef.current = versionId;
    }
  }, [appointment?.id, appointment?.meta?.versionId, appointment?.description, appointment?.comment]);

  const appointmentDescription = useMemo(() => {
    try {
      const parsed = JSON.parse(appointment.description ?? '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }, [appointment.description]);

  const debouncedSave = useDebouncedCallback(async (text: string, json: unknown) => {
    if (!appointment.id) return;

    const jsonStr = JSON.stringify(json);

    // Skip if this is the initial sync from TipTap loading the value
    if (initialContentRef.current === null) {
      initialContentRef.current = { text, jsonStr };
      return;
    }

    // Skip if content hasn't changed
    if (text === initialContentRef.current.text && jsonStr === initialContentRef.current.jsonStr) {
      return;
    }

    const operations: PatchOperation[] = [
      { op: 'add', path: '/description', value: jsonStr },
      { op: 'add', path: '/comment', value: text },
    ];

    if (appointment.meta?.versionId) {
      operations.push({ op: 'test' as const, path: '/meta/versionId', value: appointment.meta.versionId });
    }

    try {
      setSaveStatus('saving');
      await medplum.patchResource('Appointment', appointment.id, operations);
      initialContentRef.current = { text, jsonStr };

      setSaveStatus('saved');

      if (savedTimeoutRef.current) {
        clearTimeout(savedTimeoutRef.current);
      }

      savedTimeoutRef.current = setTimeout(() => setSaveStatus('idle'), 1000);
    } catch (err) {
      setSaveStatus('idle');
      alert.error({
        title: 'Failed to update appointment notes',
        message: err instanceof Error ? err.message : 'An error occurred while updating the appointment notes',
      });
    }
  }, 700);

  return (
    <>
      <Button
        p="0"
        color="black"
        variant="transparent"
        leftSection={isOpen ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
        onClick={() => setIsOpen(!isOpen)}
      >
        <LabelText>Appointment notes</LabelText>
      </Button>

      <Collapse in={isOpen} w="100%">
        <Box pos="relative">
          <RichTextInput
            value={appointmentDescription}
            onChange={debouncedSave}
            contentMinHeight="100px"
            contentMaxHeight="200px"
            editorKey={appointment.meta?.versionId}
          />
          {saveStatus !== 'idle' && (
            <Box pos="absolute" bottom={4} right={4} display="block" style={{ alignItems: 'center', gap: 4 }}>
              {saveStatus === 'saving' && <Loader size="xs" />}
              {saveStatus === 'saved' && <IconCircleCheck size={18} color="var(--mantine-color-green-6)" />}
            </Box>
          )}
        </Box>
      </Collapse>
    </>
  );
}

function AppointmentHeader({ group, activityCodeSystem, onClick }: AppointmentHeaderProps) {
  const { patientId } = useParams();

  const appointmentItem = group.items[0];
  const appointment = appointmentItem.appointment;

  const patients = extractPatientsFromAppointment(appointment);
  const practitioners = extractPractitionersFromAppointment(appointment);
  const participants = ([...practitioners, ...patients] as (Practitioner | Patient)[]).filter(
    (participant) => resolveId(participant) !== patientId
  );
  const appointmentType = getAppointmentTypeText(appointment, activityCodeSystem);
  // A no-show was a real booking the patient missed, so only a cancelled or
  // moved visit is struck through.
  const outcome = getAppointmentOutcome(appointment);
  const isCancelled = outcome === 'cancelled' || outcome === 'rescheduled';

  return (
    <Group
      justify="space-between"
      align="center"
      onClick={() => onClick?.(group.id)}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <Group>
        <Flex gap="xs" align="center">
          <Stack gap={2}>
            <Flex gap="xs" align="center">
              <Text
                size="md"
                fw={700}
                td={isCancelled ? 'line-through' : undefined}
                c={isCancelled ? 'dimmed' : undefined}
                style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {appointmentType}
              </Text>
              {outcome && (
                <Badge variant="light" color={OUTCOME_BADGE_COLORS[outcome]} size="sm" data-outcome={outcome}>
                  {getAppointmentStatusDisplay(appointment)}
                </Badge>
              )}
            </Flex>
          </Stack>
        </Flex>
      </Group>
      <Group gap="md" align="center">
        {participants && participants.length > 0 && (
          <Avatar.Group>
            {participants.slice(0, 3).map((participant) => (
              <TeammateAvatar
                key={participant.id}
                practitioner={participant}
                size={30}
                label={formatHumanName(participant.name?.[0]) ?? 'Participant'}
              />
            ))}
            {participants.length > 3 && (
              <Tooltip
                withArrow
                label={participants.slice(3).map((participant) => (
                  <Text key={participant.id}>{formatHumanName(participant.name?.[0]) ?? 'Participant'}</Text>
                ))}
              >
                <Avatar size="30" radius="xl" bg="gray.2">
                  {`${participants.length - 3}+`}
                </Avatar>
              </Tooltip>
            )}
          </Avatar.Group>
        )}
      </Group>
    </Group>
  );
}

function ZoomHostLinkDisplay({ isLoading, isWithinWindow, isPast, hostUrl }: ZoomHostLinkDisplayProps) {
  if (!isWithinWindow) {
    return (
      <Text size="sm" c="dimmed">
        {isPast ? 'Meeting already happened' : 'Hidden until 1 hour before'}
      </Text>
    );
  }

  if (isLoading) {
    return <Loader size="sm" />;
  }

  if (hostUrl) {
    return (
      <Flex align="center" gap="xs">
        <Anchor href={hostUrl} target="_blank" rel="noopener noreferrer" w={{ lg: 300, xl: 500 }}>
          <Text truncate="end" c="cyan.6">
            {hostUrl}
          </Text>
        </Anchor>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          onClick={() => {
            navigator.clipboard.writeText(hostUrl);
            alert.success('Copied to clipboard');
          }}
        >
          <IconCopy size={14} />
        </ActionIcon>
      </Flex>
    );
  }

  return (
    <Text size="sm" c="dimmed">
      Unable to load host link
    </Text>
  );
}

function VideoCallContactType({ joinUrl, appointmentStart }: VideoCallContactTypeProps) {
  const {
    hostUrl,
    isLoading: isHostLoading,
    isWithinOneHour: isWithinHostWindow,
    isPast: isHostPast,
  } = useZoomHostLink(joinUrl, appointmentStart);

  const showJoinButton = isHostLoading || Boolean(hostUrl);

  return (
    <Stack align="flex-start">
      <Flex gap="xs" align="center">
        <Text size="md">Video call</Text>
        {showJoinButton && (
          <Button
            px={12}
            color="indigo.6"
            size="compact-xs"
            loading={isHostLoading}
            rightSection={<IconExternalLink size={14} />}
            component={isHostLoading ? 'button' : 'a'}
            href={!isHostLoading && hostUrl ? hostUrl : undefined}
            target={!isHostLoading && hostUrl ? '_blank' : undefined}
            rel={!isHostLoading && hostUrl ? 'noopener noreferrer' : undefined}
          >
            Join video call
          </Button>
        )}
      </Flex>

      {joinUrl && (
        <Flex align="start" gap="xs" direction="column">
          <Text size="sm" fw={500}>
            Host URL
          </Text>
          <ZoomHostLinkDisplay
            isLoading={isHostLoading}
            isWithinWindow={isWithinHostWindow}
            isPast={isHostPast}
            hostUrl={hostUrl}
          />
        </Flex>
      )}

      {joinUrl && (
        <Flex align="start" gap="xs" direction="column">
          <Text size="sm" fw={500}>
            Zoom meeting URL
          </Text>
          <Flex align="center" gap="xs">
            <Anchor component={Link} to={joinUrl} w={{ lg: 300, xl: 500 }}>
              <Text truncate="end" c="cyan.6">
                {joinUrl}
              </Text>
            </Anchor>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(joinUrl);
                alert.success('Copied to clipboard');
              }}
            >
              <IconCopy size={14} />
            </ActionIcon>
          </Flex>
        </Flex>
      )}

      {!joinUrl && <Text>No video call URL available</Text>}
    </Stack>
  );
}

function AppointmentContactType({ appointmentItem, isIvTherapy, partner }: AppointmentContactTypeProps) {
  if (isIvTherapy) {
    const address = partner?.address?.[0]?.text;
    return (
      <Stack>
        <Text size="md">In-person</Text>
        {address && <Text>{address}</Text>}
      </Stack>
    );
  }

  if (!appointmentItem.contactType) {
    return null;
  }

  // A cancelled visit keeps its contact type as a record, but no join button:
  // it must not read as a call someone can still start.
  if (appointmentItem.contactType === 'Video Call' && appointmentItem.appointment.status === 'cancelled') {
    return <Text size="md">Video call</Text>;
  }

  if (appointmentItem.contactType === 'Video Call') {
    return (
      <VideoCallContactType
        joinUrl={appointmentItem.virtualServiceUrl}
        appointmentStart={appointmentItem.appointment.start}
      />
    );
  }

  if (appointmentItem.contactType === 'Phone Call') {
    return (
      <Group>
        <Text size="md">Phone call</Text>
        <Text>{appointmentItem.virtualServicePhone}</Text>
      </Group>
    );
  }

  if (appointmentItem.contactType === 'In-Person') {
    return (
      <Stack>
        <Text size="md">In-person ({appointmentItem.location?.locationName ?? 'Unknown location'})</Text>
        {appointmentItem.location?.address && <Text>{formatAddress(appointmentItem.location.address)}</Text>}
      </Stack>
    );
  }

  return null;
}

export function AppointmentTimelineItem(props: AppointmentTimelineItemProps) {
  const { group, highlightRef, collapsed = false, activityCodeSystem, onCollapseToggle, onStatusChange } = props;

  const appointmentItem = group.items[0];
  const appointmentRef = getReferenceString(appointmentItem.reference);
  const appointment = appointmentItem.appointment;
  const canEditAppointment = !!onStatusChange;

  const { updateStatus, isUpdatingStatus } = useAppointment(appointment, onStatusChange);
  const {
    currentUserPermissions: { canCreatePatientAccessNote },
  } = useCurrentUser();

  // A Patient Access rep charts the intro call with a call-script card, not with an
  // Encounter — so they get a one-click Start instead of the generic chart-note
  // link. Every other role keeps that link.
  const patientAccessCardKind = patientAccessCardKindForUser(appointment, canCreatePatientAccessNote);

  const isIvTherapy = getCodeableConceptCode(appointment.appointmentType) === IV_THERAPY_APPOINTMENT_TYPE_CODE;
  const partner = appointment.supportingInformation?.find((info) => isResource(info.resource, 'Organization'))
    ?.resource as AppointmentOrganizationFragment | undefined;

  const rows = [
    [<LabelText>{formatToAdobeSpectrumFormat(appointment.start)}</LabelText>],
    [<LabelText>Does not repeat</LabelText>],
    [
      <LabelText>Status:</LabelText>,
      canEditAppointment ? (
        <Menu position="top-start" withArrow withinPortal key="status-menu" styles={{ item: { fontSize: '0.75rem' } }}>
          <Menu.Target>
            <Badge
              variant="light"
              color="blue"
              size="sm"
              fz="10px"
              rightSection={<IconChevronDown size={10} />}
              style={{ cursor: isUpdatingStatus ? 'not-allowed' : 'pointer' }}
            >
              {getAppointmentStatusDisplay(appointment)}
            </Badge>
          </Menu.Target>
          <Menu.Dropdown style={{ zIndex: 1100 }} py="xs">
            {APPOINTMENT_STATUS_OPTIONS.map((option: (typeof APPOINTMENT_STATUS_OPTIONS)[number]) => {
              const isCurrentStatus = isAppointmentStatusOptionSelected(appointment, option.value);
              return (
                <Menu.Item
                  key={option.value}
                  onClick={() => updateStatus(option.value)}
                  disabled={isUpdatingStatus || isCurrentStatus}
                >
                  {option.label}
                </Menu.Item>
              );
            })}
          </Menu.Dropdown>
        </Menu>
      ) : (
        <Badge key="status-badge" variant="light" color="blue" size="sm" fz="10px">
          {getAppointmentStatusDisplay(appointment)}
        </Badge>
      ),
    ],
    [
      <LabelText>Appointment type:</LabelText>,
      <Text size="md">{getAppointmentTypeText(appointment, activityCodeSystem)}</Text>,
    ],
    ...[isIvTherapy ? [<LabelText>Partner:</LabelText>, <Text size="md">{partner?.organizationName}</Text>] : []],
    [
      <LabelText>Chart:</LabelText>,
      patientAccessCardKind ? (
        <PatientAccessChartLink appointment={appointment} kind={patientAccessCardKind} />
      ) : (
        <ChartingLink appointment={appointment} />
      ),
    ],
    [
      <LabelText>Contact type:</LabelText>,
      <AppointmentContactType appointmentItem={appointmentItem} isIvTherapy={isIvTherapy} partner={partner} />,
    ],
  ];

  return (
    <HighlightableItem itemRef={appointmentRef!} highlightRef={highlightRef}>
      <TimelineCard
        header={<AppointmentHeader group={group} activityCodeSystem={activityCodeSystem} onClick={onCollapseToggle} />}
      >
        {!collapsed && (
          <Stack gap="lg">
            <Table variant="vertical">
              <Table.Tbody>
                {rows.map((row, rowIndex) => (
                  <Table.Tr key={rowIndex}>
                    {row.map((cell, cellIndex) => (
                      <Table.Td key={`${rowIndex}-${cellIndex}`} w={cellIndex === 0 ? 240 : 'auto'} py="md">
                        {cell}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            <Stack align="flex-start">
              <AppointmentNotes appointment={appointment} />
            </Stack>
          </Stack>
        )}
      </TimelineCard>
    </HighlightableItem>
  );
}
